<template>
    <div
        class="@container/conversation-nav min-h-14 shrink-0 py-2.5 pr-2.5 pl-3 bg-slate-50/50 backdrop-blur-lg border-solid border border-white flex flex-wrap gap-1 justify-between items-center rounded-2xl absolute inset-bs-2.5 inset-s-2.5 inset-e-2 shadow z-10 mb-3 mdShortHeight:inset-bs-0 mdShortHeight:inset-s-0 mdShortHeight:inset-e-0 mdShortHeight:rounded-none mdShortHeight:border-0 mdShortHeight:border-b"
    >
        <div
            class="grid w-full grow grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0 items-start @sm/conversation-nav:grid-cols-[minmax(0,1fr)_auto_auto] @sm/conversation-nav:gap-y-2 @sm/conversation-nav:items-center"
        >
            <div class="min-w-0 flex flex-col">
                <div class="mb-1.5 md:hidden inline-flex">
                    <router-link
                        class="text-sm leading-none!"
                        :to="{
                            name: 'message-hub',
                            query: route.query,
                        }"
                    >
                        <font-awesome-icon widthAuto :icon="faArrowLeft"></font-awesome-icon>
                        Threads
                    </router-link>
                </div>
                <div v-if="activeThread" class="text-sm font-extrabold leading-none truncate">
                    {{ activeThread.contact_name || formatPhoneNumber(activeThread.contact_phone) }}
                </div>
                <div
                    v-else-if="contact.first_name || contact.phone"
                    class="text-sm font-extrabold leading-none truncate"
                >
                    {{ displayContactName(contact) }}
                </div>
                <div v-else class="text-sm font-extrabold leading-none truncate">Loading...</div>
                <div
                    v-if="
                        activeThread
                            ? activeThread.contact_phone && activeThread.contact_name
                            : contact.first_name && contact.phone
                    "
                    class="text-xs leading-tight mt-0.5 truncate"
                >
                    {{ formatPhoneNumber(activeThread ? activeThread.contact_phone : contact.phone) }}
                </div>
            </div>

            <div
                v-if="canSeeIntelligentInbox"
                class="col-span-2 col-start-1 row-start-2 flex min-w-0 flex-row items-center justify-center gap-2 justify-self-stretch @sm/conversation-nav:col-span-1 @sm/conversation-nav:col-start-2 @sm/conversation-nav:row-start-1 @sm/conversation-nav:justify-self-start"
            >
                <vx-badge v-if="sectionDefinition" :color="sectionDefinition.badgeColor" size="2xs">
                    {{ sectionDefinition.label }}
                </vx-badge>

                <div v-if="category" class="flex items-center gap-1.5" data-test="thread-category-badge">
                    <span
                        class="inline-block size-1.5 rounded-[3px] shrink-0 @sm/conversation-nav:size-2.5"
                        :style="{ backgroundColor: '#' + category.color }"
                    ></span>
                    <span class="text-[11px] text-slate-600 truncate max-w-40 @sm/conversation-nav:text-xs">{{
                        category.name
                    }}</span>
                </div>
            </div>

            <div
                class="col-start-2 row-start-1 flex items-center gap-2 justify-self-end @sm/conversation-nav:col-start-3"
            >
                <vx-button
                    v-if="onCorporate() && canUnsubscribe && contact.phone"
                    @click="unsubscribe"
                    outline
                    size="sm"
                    color="muted"
                >
                    <span class="inline md:hidden">Unsub.</span>
                    <span class="hidden md:inline">Unsubscribe</span>
                    <font-awesome-icon widthAuto :icon="faArrowRightFromBracket"></font-awesome-icon>
                </vx-button>
                <router-button
                    v-if="route.name !== 'message-hub.contact-view' && !contactNotFound"
                    :to="{
                        name: 'message-hub.contact-view',
                        params: { contactId: contact.id },
                        query: route.query,
                    }"
                    size="sm"
                    color="muted"
                >
                    <span class="hidden md:inline">Contact</span> Details
                    <font-awesome-icon widthAuto :icon="faCaretRight"></font-awesome-icon>
                </router-button>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { faArrowLeft, faArrowRightFromBracket, faCaretRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxButton } from '@voxie/frontend-components';
import { computed, inject, watch } from 'vue';
import { THREAD_SECTIONS, isThreadSectionValue } from '~/constants/messageHub';
import { useIntelligentInbox } from '~/components/message-hub/intelligent-inbox/useIntelligentInbox';
import { useConversation } from '~/composables/useConversation';
import type { Features } from '~/provides/features.types';
import { useContactDetails } from '../contacts/contact/useContactDetails';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { canUseIntelligentInbox } from '~/components/message-hub/messageHubUtil';
import { RouterLink, useRoute } from 'vue-router';
import { displayContactName, formatPhoneNumber } from '../filters';
import RouterButton from '~/components/general/RouterButton.vue';

const route = useRoute();

const FEATURES = inject('FEATURES') as Features;

const { onCorporate } = useCurrentGroup();

const canSeeIntelligentInbox = computed(() => {
    return (
        canUseIntelligentInbox(FEATURES) &&
        activeThread.value &&
        (activeThread.value.thread_section || activeThread.value.thread_category_id)
    );
});

const { activeThread } = useConversation();

const { contact, contactNotFound, canUnsubscribe, unsubscribe } = useContactDetails();

const { fetchCategories, effectiveSection, resolveCategory } = useIntelligentInbox();

watch(
    canSeeIntelligentInbox,
    (canSee) => {
        if (canSee) {
            fetchCategories();
        }
    },
    { immediate: true }
);

const sectionDefinition = computed(() => {
    const section = activeThread.value ? effectiveSection(activeThread.value) : undefined;
    if (section === 'pending') {
        return undefined;
    }
    return isThreadSectionValue(section) ? THREAD_SECTIONS[section] : undefined;
});

const category = computed(() => {
    const categoryId = activeThread.value?.thread_category_id;
    if (categoryId === null || categoryId === undefined) {
        return undefined;
    }
    return resolveCategory(categoryId);
});
</script>
